import { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { CommentSortEnum } from '../types'
import { isCommentBeforeWithObjects, sortComments } from './comment-sorting'

export const deleteCommentInLinkedList = (
  comments: CommentInterface[],
  idToDelete: number,
): CommentInterface[] => {
  const map = new Map<number, CommentInterface>(comments.map(c => [c.id, { ...c }]))

  const victim = map.get(idToDelete)
  if (!victim) return comments

  const { prevId, nextId } = victim

  if (prevId != null) {
    const prev = map.get(prevId)
    if (prev) prev.nextId = nextId ?? null
  }
  if (nextId != null) {
    const next = map.get(nextId)
    if (next) next.prevId = prevId ?? null
  }

  map.delete(idToDelete)
  return [...map.values()]
}

/**
 * Determines the optimal position for a comment in the linked list
 * @param comments The list of comments
 * @param comment The comment to position
 * @returns The optimal prevId for insertion
 */
export const findOptimalCommentPosition = (
  comments: CommentInterface[],
  comment: CommentInterface,
  sortOption: CommentSortEnum,
): number | null => {
  if (comments.length === 0) {
    return null
  }

  const originalIndex = comments.findIndex(c => c.id === comment.id)
  if (originalIndex !== -1) {
    return comments[originalIndex].prevId
  }

  const sortedComments = sortComments(comments, sortOption)
  let prevComment = null

  for (const sortedComment of sortedComments) {
    if (isCommentBeforeWithObjects(comment, sortedComment, sortOption)) {
      break
    }
    prevComment = sortedComment
  }

  return prevComment?.id ?? null
}

export const insertCommentToLinkedList = (
  comments: CommentInterface[],
  newComment: CommentInterface,
  prevId: number | null,
): CommentInterface[] => {
  const list = comments.map(c => ({ ...c }))

  const insertIndex =
    prevId === null
      ? 0
      : (() => {
          const idx = list.findIndex(c => c.id === prevId)
          if (idx === -1) throw new Error(`prevId ${prevId} not found`)
          return idx + 1
        })()

  const prev = insertIndex > 0 ? list[insertIndex - 1] : null
  const next = insertIndex < list.length ? list[insertIndex] : null

  const comment: CommentInterface = {
    ...newComment,
    prevId: prev ? prev.id : null,
    nextId: next ? next.id : null,
  }

  if (prev) prev.nextId = comment.id
  if (next) next.prevId = comment.id

  list.splice(insertIndex, 0, comment)
  return list
}

export const getLastCommentId = (comments: CommentInterface[]): number | null => {
  return comments.length > 0 ? comments[comments.length - 1].id : null
}
